import { useState, useEffect, useCallback, useRef } from 'react';
import { InteractionManager } from 'react-native';
import { parseNCFileContent } from '@components/common/NCFile/helpers/parseNCFileContent';
import { prepareNCFileListItems } from '@components/common/NCFile/helpers/prepareNCFileListItems';
import { NCListItem, NCParsedLine, UseManageNCTextVirtualizedParams } from '@components/common/NCFile/types';
import { useScrollTopButton } from './useScrollTopButton';

export const useManageNCTextVirtualized = ({ data, initialCollapsed, withoutCollapsingFeatureAndStyles }: UseManageNCTextVirtualizedParams) => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [parsedLines, setParsedLines] = useState<NCParsedLine[]>([]);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [preparedListItems, setPreparedListItems] = useState<NCListItem[]>([]);
  const { showScrollTop, handleScroll } = useScrollTopButton();
  const totalSectionsRef = useRef(0);

  // Parse data deferred after interactions settle
  useEffect(() => {
    setIsProcessing(true);
    setExpandedSections(new Set());

    const handle = InteractionManager.runAfterInteractions(() => {
      const lines = parseNCFileContent(data, { withCollapsing: !withoutCollapsingFeatureAndStyles });

      // Count total tool sections
      let maxSectionIndex = -1;
      for (const line of lines) {
        if (line.isToolCall && line.toolSectionIndex !== null && line.toolSectionIndex > maxSectionIndex) {
          maxSectionIndex = line.toolSectionIndex;
        }
      }
      totalSectionsRef.current = maxSectionIndex + 1;

      setParsedLines(lines);

      // Initialize expanded state based on initialCollapsed
      if (!initialCollapsed && maxSectionIndex >= 0) {
        const allSections = new Set<number>();
        for (let i = 0; i <= maxSectionIndex; i++) {
          allSections.add(i);
        }
        setExpandedSections(allSections);
      }

      setIsProcessing(false);
    });

    return () => handle.cancel();
  }, [data, withoutCollapsingFeatureAndStyles]);

  // Rebuild list items when parsed lines or expanded sections change
  useEffect(() => {
    if (parsedLines.length === 0) {
      setPreparedListItems([]);
      return;
    }
    const items = prepareNCFileListItems(parsedLines, expandedSections);
    setPreparedListItems(items);
  }, [parsedLines, expandedSections]);

  const toggleSection = useCallback((sectionIndex: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionIndex)) {
        next.delete(sectionIndex);
      } else {
        next.add(sectionIndex);
      }
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
  }, []);

  const expandAll = useCallback(() => {
    const allSections = new Set<number>();
    for (let i = 0; i < totalSectionsRef.current; i++) {
      allSections.add(i);
    }
    setExpandedSections(allSections);
  }, []);

  return {
    isProcessing,
    preparedListItems,
    expandedSections,
    showScrollTop,
    toggleSection,
    collapseAll,
    expandAll,
    handleScroll,
  };
};
